import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as memoryApi from '@/api/issue-memory.api';

/**
 * Query keys for issue memory
 */
export const issueMemoryKeys = {
  all: ['issue-memory'] as const,
  list: (params: memoryApi.IssueListParams) => [...issueMemoryKeys.all, 'list', params] as const,
  issue: (project: number | string, iid: number | string) => [...issueMemoryKeys.all, 'issue', String(project), String(iid)] as const,
  tokens: () => [...issueMemoryKeys.all, 'tokens'] as const,
  overview: (days: number) => [...issueMemoryKeys.all, 'overview', days] as const,
};

/**
 * The team's last days: issues worked, closed and done with AI (executives and PMs)
 */
export function useOverview(days: number) {
  return useQuery({
    queryKey: issueMemoryKeys.overview(days),
    queryFn: () => memoryApi.getOverview(days),
    staleTime: 60 * 1000,
  });
}

/**
 * Issues for the board (refreshed every minute: webhooks change them)
 */
export function useIssueList(params: memoryApi.IssueListParams) {
  return useQuery({
    queryKey: issueMemoryKeys.list(params),
    queryFn: () => memoryApi.listIssues(params),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useIssueMemory(project: string | undefined, iid: string | undefined) {
  return useQuery({
    queryKey: issueMemoryKeys.issue(project || '', iid || ''),
    queryFn: () => memoryApi.getIssue(project!, iid!),
    enabled: !!project && !!iid,
    staleTime: 30 * 1000,
  });
}

export function useIssueDossier(project: string | undefined, iid: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...issueMemoryKeys.issue(project || '', iid || ''), 'dossier'],
    queryFn: () => memoryApi.getDossier(project!, iid!),
    enabled: enabled && !!project && !!iid,
  });
}

export function useRelatedIssues(project: string | undefined, iid: string | undefined) {
  return useQuery({
    queryKey: [...issueMemoryKeys.issue(project || '', iid || ''), 'related'],
    queryFn: () => memoryApi.getRelated(project!, iid!),
    enabled: !!project && !!iid,
  });
}

export function useIssueHistory(project: string | undefined, iid: string | undefined) {
  return useQuery({
    queryKey: [...issueMemoryKeys.issue(project || '', iid || ''), 'history'],
    queryFn: () => memoryApi.getHistory(project!, iid!),
    enabled: !!project && !!iid,
  });
}

export function useApiTokens() {
  return useQuery({
    queryKey: issueMemoryKeys.tokens(),
    queryFn: memoryApi.listTokens,
  });
}

export function useCreateApiToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: memoryApi.createToken,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: issueMemoryKeys.tokens() }),
  });
}

export function useRevokeApiToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: memoryApi.revokeToken,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: issueMemoryKeys.tokens() }),
  });
}

/** Who can be assigned in the issue's project (executives and PMs) */
export function useAssignable(project: string | undefined, iid: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...issueMemoryKeys.all, 'assignable', project || ''],
    queryFn: () => memoryApi.getAssignable(project!, iid!),
    enabled: enabled && !!project && !!iid,
    staleTime: 5 * 60 * 1000,
  });
}

/** After a change sent to GitLab: its webhook updates the memory a moment later; the project pages read GitLab */
function useRefreshAfterGitLab(project: string | undefined, iid: string | undefined) {
  const queryClient = useQueryClient();
  return () => {
    const refresh = () => {
      queryClient.invalidateQueries({ queryKey: issueMemoryKeys.issue(project || '', iid || '') });
      queryClient.invalidateQueries({ queryKey: [...issueMemoryKeys.all, 'list'] });
      queryClient.invalidateQueries({ queryKey: ['issue-detail'] });
      queryClient.invalidateQueries({ queryKey: ['project-dashboard'] });
    };
    refresh();
    setTimeout(refresh, 3000);
  };
}

export function useAssignIssue(project: string | undefined, iid: string | undefined) {
  const onSuccess = useRefreshAfterGitLab(project, iid);
  return useMutation({
    mutationFn: (username: string | null) => memoryApi.assignIssue(project!, iid!, username),
    onSuccess,
  });
}

export function useSetAcceptance(project: string | undefined, iid: string | undefined) {
  const onSuccess = useRefreshAfterGitLab(project, iid);
  return useMutation({
    mutationFn: (items: Array<{ text: string; done: boolean }>) => memoryApi.setAcceptance(project!, iid!, items),
    onSuccess,
  });
}

export function useAddComment(project: string | undefined, iid: string | undefined) {
  const onSuccess = useRefreshAfterGitLab(project, iid);
  return useMutation({
    mutationFn: (body: string) => memoryApi.addComment(project!, iid!, body),
    onSuccess,
  });
}

export function useSetGoal(project: string | undefined, iid: string | undefined) {
  const onSuccess = useRefreshAfterGitLab(project, iid);
  return useMutation({
    mutationFn: (goal: string) => memoryApi.setGoal(project!, iid!, goal),
    onSuccess,
  });
}
